/*
 * *****************************************************************************
 *     Copyright (C)  Motorola Solutions, INC.
 *     All Rights Reserved.
 *     Motorola Solutions Confidential Restricted.
 * *****************************************************************************
 */

import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { ccHubInitialized } from '../+state/cchub.actions';
import {
    selectAuxRelayState,
    selectCCHubAttached,
    selectCCHubHeadset1Connected,
    selectCCHubHeadset2Connected,
    selectRadioHeadsetConnected,
    selectRpiExternalJackSenseState
} from '../+state/cchub.selectors';
import { filter, skip, take, takeUntil } from 'rxjs/operators';
import { CcHubAudioService } from './cc-hub-audio.service';
import { WebUsbSwdlService } from './web-usb-swdl.service';
import { displayToastNotification } from '../../notification/+state/notification.actions';
import { ToastType } from '@msi/cobalt';
import { WebPeripheralNotifier } from '../../usb/cc-hub/lib/web-usb-module/web-peripheral-notifier';
import { GlobalObjectsProvider } from 'web-usb-common';
import { AuxliaryOutputsNames, DigitalSignatureVerifierBrowser } from 'web-usb-module';
import { Diagnostics } from 'web-audio-diagnostics';
import { selectLlrState, selectOffHookState } from '../+state/call.selectors';
import { Subject } from "rxjs";

@Injectable({
    providedIn: 'root'
})
export class CchubService {
    public readonly VENDOR_ID = 0x0cad;
    public readonly PRODUCT_ID = 0x1807;
    private device!: USBDevice;

    // Web Audio Module
    private webPeripheralNotifier!: WebPeripheralNotifier;
    private static globalObjectProvider: GlobalObjectsProvider;
    private hasPermission = Promise.withResolvers<boolean>();
    private readonly gestureListener: () => Promise<void> = () =>
        navigator.usb
            .requestDevice({ filters: [{ vendorId: this.VENDOR_ID, productId: this.PRODUCT_ID }] })
            .then((device) => (this.device = device))
            .then(() => window.removeEventListener('click', this.gestureListener))
            .then(() => this.hasPermission.resolve(true))
            .catch((e) => console.error(e));

    private unsubscribe$ = new Subject<void>();

    constructor(
        private store: Store,
        private ccHubAudioService: CcHubAudioService,
        private webUsbSwdlService: WebUsbSwdlService
    ) {
        this.configureGlobalObjectProvider();

        this.store
            .select(selectCCHubAttached)
            .pipe(
                filter((val) => val),
                take(1)
            )
            .subscribe(() => {
                console.debug('CCHub Detected. Beginning Initialization.');
                this.checkPermissions();
                this.webPeripheralNotifier = new WebPeripheralNotifier(this.store);
                this.webPeripheralNotifier.onCCHubError((error: any) => console.error('CCHub error: ', error));
                this.hasPermission.promise
                    .then(() => this.webPeripheralNotifier.startCCHub())
                    .then(() => this.webUsbSwdlService.init())
                    .then(() => this.setRpiArbitration())
                    .then(() => {
                        this.monitorRpiJackSenseState();
                        this.monitorCcHubLlrState();
                        this.monitorCcHubOffHookState();
                        this.monitorCcHubHeadsets();
                        this.monitorCcHubAuxRelayState();
                    })
                    .then(() => this.store.dispatch(ccHubInitialized()))
                    .then(() => window.addEventListener('beforeunload', () => this.disconnect()))
                    .then(() => this.ccHubAudioService.init())
                    .catch((reason) => console.error(`Failed to initialize CC_HUB ${JSON.stringify(reason, undefined, 1)}`));
            });
    }

    // Required for utilizing the solar-axis library while by-passing the wam-wrapper
    private configureGlobalObjectProvider(): void {
        CchubService.globalObjectProvider = GlobalObjectsProvider.getInstance();
        CchubService.globalObjectProvider.setStorage(localStorage);
        CchubService.globalObjectProvider.setNavigator(navigator);
        CchubService.globalObjectProvider.setDiagnostics(Diagnostics.getInstance());
        CchubService.globalObjectProvider.setWindow(window);
        CchubService.globalObjectProvider.setRandomNumberGenerator(window.crypto);
        CchubService.globalObjectProvider.setSignatureVerifier(new DigitalSignatureVerifierBrowser());
    }

    protected checkPermissions() {
        navigator.usb
            .getDevices()
            .then((devices) => (this.device = devices.find((d) => d.vendorId === this.VENDOR_ID && d.productId === this.PRODUCT_ID) as USBDevice))
            .then(() => (this.device ? this.hasPermission.resolve(true) : window.addEventListener('click', this.gestureListener)))
            .catch((e) => console.error(e));
    }

    private setRpiArbitration() {
        return this.webPeripheralNotifier
            .sendOutputs(AuxliaryOutputsNames.Radio_Phone_Interface_Arbitration_select, true)
            .then(() => console.debug(`Enabled ${AuxliaryOutputsNames.Radio_Phone_Interface_Arbitration_select} Relay`))
            .catch((e) => console.error('Failed to set RPI arbitration mode', e));
    }

    private monitorRpiJackSenseState() {
        this.store.pipe(selectRpiExternalJackSenseState).subscribe((on) =>
            this.webPeripheralNotifier
                .sendOutputs(AuxliaryOutputsNames.Radio_Phone_Interface_Jack_sense, on)
                .then(() => console.debug(`${on ? 'Enabled' : 'Disabled'} ${AuxliaryOutputsNames.Radio_Phone_Interface_Jack_sense} Relay`))
                .catch((e) => console.error(`Failed to ${on ? 'enable' : 'disable'} ${AuxliaryOutputsNames.Radio_Phone_Interface_Jack_sense} Relay`, e)));
    }

    private monitorCcHubOffHookState() {
        this.store.pipe(selectOffHookState).subscribe((on) =>
            this.webPeripheralNotifier
                .sendOutputs(AuxliaryOutputsNames.Radio_Phone_Interface_Off_hook, on)
                .then(() => console.debug(`${on ? 'Enabled' : 'Disabled'} ${AuxliaryOutputsNames.Radio_Phone_Interface_Off_hook} Relay`))
                .catch((e) => console.error(`Failed to ${on ? 'enable' : 'disable'} ${AuxliaryOutputsNames.Radio_Phone_Interface_Off_hook} Relay`, e))
        );
    }

    private monitorCcHubLlrState() {
        this.store.pipe(selectLlrState).subscribe((on) => {
            this.webPeripheralNotifier
                .sendOutputs(AuxliaryOutputsNames.LLR_1_output, on)
                .then(() => console.debug(`${on ? 'Enabled' : 'Disabled'} ${AuxliaryOutputsNames.LLR_1_output} Relay`))
                .catch((e) => console.error(`Failed to ${on ? 'enable' : 'disable'} ${AuxliaryOutputsNames.LLR_1_output} Relay`, e));
            this.webPeripheralNotifier
                .sendOutputs(AuxliaryOutputsNames.LLR_2_output, on)
                .then(() => console.debug(`${on ? 'Enabled' : 'Disabled'} ${AuxliaryOutputsNames.LLR_2_output} Relay`))
                .catch((e) => console.error(`Failed to ${on ? 'enable' : 'disable'} ${AuxliaryOutputsNames.LLR_2_output} Relay`, e));
            this.webPeripheralNotifier
                .sendOutputs(AuxliaryOutputsNames.LLR_3_output, on)
                .then(() => console.debug(`${on ? 'Enabled' : 'Disabled'} ${AuxliaryOutputsNames.LLR_3_output} Relay`))
                .catch((e) => console.error(`Failed to ${on ? 'enable' : 'disable'} ${AuxliaryOutputsNames.LLR_3_output} Relay`, e));
        });
    }

    public monitorCcHubAuxRelayState() {
        this.store.select(selectAuxRelayState(AuxliaryOutputsNames.Auxiliary_output_1))
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe((on) =>
                this.webPeripheralNotifier
                    .sendOutputs(AuxliaryOutputsNames.Auxiliary_output_1, on)
                    .then(() => console.debug(`${on ? 'Enabled' : 'Disabled'} ${AuxliaryOutputsNames.Auxiliary_output_1} Relay`))
                    .catch((e) => console.error(`Failed to ${on ? 'enabled' : 'disabled'} ${AuxliaryOutputsNames.Auxiliary_output_1} Relay`, e))
            );
        this.store.select(selectAuxRelayState(AuxliaryOutputsNames.Auxiliary_output_2))
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe((on) =>
                this.webPeripheralNotifier
                    .sendOutputs(AuxliaryOutputsNames.Auxiliary_output_2, on)
                    .then(() => console.debug(`${on ? 'Enabled' : 'Disabled'} ${AuxliaryOutputsNames.Auxiliary_output_2} Relay`))
                    .catch((e) => console.error(`Failed to ${on ? 'enabled' : 'disabled'} ${AuxliaryOutputsNames.Auxiliary_output_2} Relay`, e))
            );
        this.store.select(selectAuxRelayState(AuxliaryOutputsNames.Auxiliary_output_3))
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe((on) =>
                this.webPeripheralNotifier
                    .sendOutputs(AuxliaryOutputsNames.Auxiliary_output_3, on)
                    .then(() => console.debug(`${on ? 'Enabled' : 'Disabled'} ${AuxliaryOutputsNames.Auxiliary_output_3} Relay`))
                    .catch((e) => console.error(`Failed to ${on ? 'enabled' : 'disabled'} ${AuxliaryOutputsNames.Auxiliary_output_3} Relay`, e))
            );
        this.store.select(selectAuxRelayState(AuxliaryOutputsNames.Auxiliary_output_4))
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe((on) =>
                this.webPeripheralNotifier
                    .sendOutputs(AuxliaryOutputsNames.Auxiliary_output_4, on)
                    .then(() => console.debug(`${on ? 'Enabled' : 'Disabled'} ${AuxliaryOutputsNames.Auxiliary_output_4} Relay`))
                    .catch((e) => console.error(`Failed to ${on ? 'enabled' : 'disabled'} ${AuxliaryOutputsNames.Auxiliary_output_4} Relay`, e))
            );
    }

    private monitorCcHubHeadsets() {
        this.store
            .select(selectCCHubHeadset1Connected)
            .pipe(skip(1))
            .subscribe((connected) =>
                connected
                    ? this.store.dispatch(displayToastNotification({ level: ToastType.success, message: 'Headset 1 Connected.' }))
                    : this.store.dispatch(displayToastNotification({ level: ToastType.warning, message: 'Headset 1 Disconnected.' }))
            );
        this.store
            .select(selectCCHubHeadset2Connected)
            .pipe(skip(1))
            .subscribe((connected) =>
                connected
                    ? this.store.dispatch(displayToastNotification({ level: ToastType.success, message: 'Headset 2 Connected.' }))
                    : this.store.dispatch(displayToastNotification({ level: ToastType.warning, message: 'Headset 2 Disconnected.' }))
            );
        this.store
            .select(selectRadioHeadsetConnected)
            .pipe(skip(1))
            .subscribe((connected) =>
                connected
                    ? this.store.dispatch(displayToastNotification({ level: ToastType.success, message: 'Radio Headset Connected.' }))
                    : this.store.dispatch(displayToastNotification({ level: ToastType.warning, message: 'Radio Headset Disconnected.' }))
            );
    }

    private disconnect(): void {
        this.unsubscribe$.next();
        this.unsubscribe$.complete();

        this.webPeripheralNotifier.disconnectCCHub();
    }
}
